import { EffectsCommandMap } from 'dva';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import { history } from '@@/core/history';
import dayjs from 'dayjs';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import fMessage from '@/components/fMessage';
import { handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2 } from '@/utils/service';
import {
  ICollectionCreateVersionDraftType,
  IResourceCreateVersionDraftType,
} from '@/type/resourceTypes';
import type {
  ChangeAction,
  CollectionManagerState,
  Version_OnMount_Page_Action,
  Version_OnUnmount_Page_Action,
  Version_FetchDataSourceAction,
  Version_Fetch_CollectionItems_Action,
  Version_SyncAllPropertiesAction,
  Version_SaveDraft_Action,
  Version_SaveDate_Action,
  Version_SetRssSource_Action,
} from './types';
import { isRssRelatedResource } from '@/utils/rss';

const versionInitPayload = {
  version_pageState: 'loading',
  version_resourceID: '',
  version_version: '',
  version_versions: [],
  version_resourceInfo: null,
  version_resourceTypeConfig: {
    isSupportDownload: false,
    isSupportOptionalConfig: false,
    uploadEntry: [] as ('collectionLibrary' | 'podcastRss')[],
  },
  version_collectionItemsSetting: {
    collection_sort_list: 'collection_sort_ascending',
    collection_item_title: 'collection_item_title_rtitle',
    collection_item_no_display: 'collection_item_no_display_show',
    collection_item_image_display: 'collection_item_image_display_show',
    collection_item_descr_display: 'collection_item_descr_display_show',
    collection_view: 'collection_view_list',
  },
  version_resourceKeywords: '',
  version_collectionItems: [],
  version_resources_state: 'loading',
  version_resources_totalCount: 0,
  version_resources_pageCurrent: 1,
  version_resources_pageSize: 10,
  version_descriptionText: '',
  version_graphShow: true,
  version_systemProperties: [],
  version_propertyKeysSort: [],
  version_customProperties: [],
  version_customConfigurations: [],
  version_directDependencies: [],
  version_baseUpcastResources: [],
  version_authExcludedItems: [],
  version_reload: 0,
  version_draftSaving: false,
  version_draftSaveTime: '',
  version_collectionItemsChanged: false,
  version_otherChanged: false,
};

export const versionEffects = {
  *version_onMount_Page(
    { payload }: Version_OnMount_Page_Action,
    { call, put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_resourceID: payload.resourceID,
        version_version: payload.version,
      },
    });

    yield put<Version_FetchDataSourceAction>({
      type: 'version_fetchDataSource',
    });
  },
  *version_onUnmount_Page({}: Version_OnUnmount_Page_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: versionInitPayload,
    });
  },
  *version_fetchDataSource(
    {}: Version_FetchDataSourceAction,
    { call, put, select }: EffectsCommandMap,
  ) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
      resourceIdOrName: collectionManager.version_resourceID,
      isLoadPolicyInfo: 1,
      isTranslate: 1,
    };
    const {
      data: data_resourceInfo,
    }: {
      data: {
        userId: number;
        status: number;
        resourceId: string;
        resourceName: string;
        resourceVersions: {
          version: string;
        }[];
        latestVersion: string;
        coverImages: string[];
        resourceType: string[];
        resourceTypeCode: string;
        policies: PolicyFullInfo_Type[];
        baseUpcastResources: {
          resourceId: string;
          resourceName: string;
        }[];
        serializeStatus: 0 | 1;
        feedUrl?: string;
        rssGuid?: string;
        rssPubDate?: string;
      };
    } = yield call(FServiceAPI.Resource.info, params1);

    // RSS 相关资源停用一切草稿读写，避免草稿覆盖接口下发的属性/描述
    const isRssRelated = isRssRelatedResource(data_resourceInfo);
    let data_draft: null | {
      resourceId: string;
      updateDate: string;
      draftData: ICollectionCreateVersionDraftType;
    } = null;
    if (!isRssRelated) {
      const params6: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
        resourceId: collectionManager.version_resourceID,
      };
      const draftResult: {
        data: null | {
          resourceId: string;
          updateDate: string;
          draftData: ICollectionCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.lookDraft, params6);
      data_draft = draftResult.data;
    }

    let resourceSelectedVersion: string = collectionManager.version_version;
    if (data_resourceInfo.latestVersion === '') {
      resourceSelectedVersion = '';
    } else {
      if (resourceSelectedVersion === '') {
        resourceSelectedVersion = data_resourceInfo.latestVersion;
      }
    }

    const params4: Parameters<typeof FServiceAPI.Resource.getResourceTypeInfoByCode>[0] = {
      code: data_resourceInfo.resourceTypeCode,
    };
    const {
      data: data_ResourceTypeInfo,
    }: {
      data: {
        resourceConfig: {
          supportDownload: 1 | 2;
          supportOptionalConfig: 1 | 2;
          fileCommitMode: number[];
        };
      };
    } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params4);
    const uploadEntry: ('collectionLibrary' | 'podcastRss')[] = [];
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode?.includes(2 ** 4)) {
      uploadEntry.push('collectionLibrary');
    }
    if (data_ResourceTypeInfo.resourceConfig.fileCommitMode?.includes(2 ** 5)) {
      uploadEntry.push('podcastRss');
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_version: resourceSelectedVersion,
        version_resourceTypeConfig: {
          isSupportDownload: data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
          isSupportOptionalConfig:
            data_ResourceTypeInfo.resourceConfig.supportOptionalConfig === 2,
          uploadEntry,
        },
      },
    });
    if (resourceSelectedVersion === '') {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_pageState: 'loaded',
        },
      });
      return;
    }

    const params: Parameters<typeof FServiceAPI.Resource.resourceVersionInfo1>[0] = {
      resourceId: collectionManager.version_resourceID,
      version: resourceSelectedVersion,
    };
    const {
      data: data_versionInfo,
    }: {
      data: {
        resourceId: string;
        resourceName: string;
        resourceType: string[];
        resourceTypeCode: string;
        version: string;
        fileSha1: string;
        customPropertyDescriptors: {
          key: string;
          name: string;
          defaultValue: string;
          type: 'editableText' | 'readonlyText' | 'radio' | 'checkbox' | 'select';
          candidateItems: string[];
          remark: string;
        }[];
        createDate: string;
        updateDate: string;
        description: string;
        systemPropertyDescriptors: {
          defaultValue: number | string;
          key: string;
          name: string;
          remark: string;
          valueDisplay: string;
          valueUnit: string;
          insertMode: 1 | 2;
        }[];
        dependencies: {
          resourceId: string;
          resourceName: string;
          versionRange: string;
        }[];
        catalogueProperty?: {
          collection_sort_list?: 'collection_sort_ascending' | 'collection_sort_descending';
          collection_item_title?:
            | 'collection_item_title_rtitle'
            | 'collection_item_title_sn'
            | 'collection_item_title_empty'
            | 'collection_item_title_custom';
          collection_item_no_display?:
            | 'collection_item_no_display_show'
            | 'collection_item_no_display_hide';
          collection_item_image_display?:
            | 'collection_item_image_display_show'
            | 'collection_item_image_display_hide';
          collection_item_descr_display?:
            | 'collection_item_descr_display_show'
            | 'collection_item_descr_display_hide';
          collection_view?: 'collection_view_list' | 'collection_view_card';
        };
      };
    } = yield call(FServiceAPI.Resource.resourceVersionInfo1, params);
    if (!data_versionInfo) {
      history.replace(FUtil.LinkTo.exception403({}));
      return;
    }

    const params5: Parameters<
      typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
    >[0] = {
      sha1: '',
      resourceTypeCode: data_resourceInfo.resourceTypeCode,
      inheritData: {
        additionalProperties: !!data_draft
          ? data_draft.draftData.additionalProperties.map((p) => {
              return {
                key: p.key,
                value: p.value,
              };
            })
          : data_versionInfo.systemPropertyDescriptors
              .filter((spd) => {
                return spd.insertMode === 2;
              })
              .map((spd) => {
                return {
                  key: spd.key,
                  value: spd.valueDisplay,
                };
              }),
        customProperties: !!data_draft
          ? data_draft.draftData.customProperties
          : data_versionInfo.customPropertyDescriptors
              .filter((i) => i.type === 'readonlyText')
              .map((b) => {
                return {
                  key: b.key,
                  name: b.name,
                  value: b.defaultValue,
                  description: b.remark,
                };
              }),
        customConfigurations: !!data_draft
          ? data_draft.draftData.customConfigurations
          : data_versionInfo.customPropertyDescriptors
              .filter((i) => {
                return i.type === 'editableText' || i.type === 'select';
              })
              .map((i) => ({
                key: i.key,
                name: i.name,
                description: i.remark,
                type: i.type === 'editableText' ? 'input' : 'select',
                input: i.defaultValue,
                select: i.candidateItems,
              })),
      },
    };
    const result: Awaited<
      ReturnType<typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2>
    > = yield call(handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2, params5);

    const params7: Parameters<typeof FServiceAPI.Resource.getCollectionItems_Draft>[0] = {
      resourceId: collectionManager.version_resourceID,
      limit: 1,
    };

    const {
      data: data_collectionItems,
    }: {
      data: {
        skip: number;
        limit: number;
        totalItem: number;
        dataList: [];
      };
    } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, params7);

    const params9: Parameters<typeof FServiceAPI.Resource.getCollectionCollectRules>[0] = {
      resourceId: collectionManager.version_resourceID,
    };

    const {
      data: data_CollectionCollectRules,
    }: {
      data: null | {
        status: 0 | 1;
        conditionType: 1 | 2;
        filterConditions: {
          key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
          limitOperatorType:
            | 'INCLUDES'
            | 'NOT_INCLUDES'
            | 'STARTS_WITH'
            | 'ENDS_WITH'
            | 'EQUAL'
            | 'NOT_EQUAL';
          value: string;
        }[];
      };
    } = yield call(FServiceAPI.Resource.getCollectionCollectRules, params9);

    const version_collectionItemsSetting: CollectionManagerState['version_collectionItemsSetting'] =
      !!data_draft
        ? {
            collection_sort_list:
              data_draft.draftData.collectionItemsSetting?.collection_sort_list ||
              'collection_sort_ascending',
            collection_item_title:
              data_draft.draftData.collectionItemsSetting?.collection_item_title ||
              'collection_item_title_rtitle',
            collection_item_no_display:
              data_draft.draftData.collectionItemsSetting?.collection_item_no_display ||
              'collection_item_no_display_show',
            collection_item_image_display:
              data_draft.draftData.collectionItemsSetting?.collection_item_image_display ||
              'collection_item_image_display_show',
            collection_item_descr_display:
              data_draft.draftData.collectionItemsSetting?.collection_item_descr_display ||
              'collection_item_descr_display_show',
            collection_view:
              data_draft.draftData.collectionItemsSetting?.collection_view ||
              'collection_view_list',
          }
        : {
            collection_sort_list:
              data_versionInfo.catalogueProperty?.collection_sort_list ||
              'collection_sort_ascending',
            collection_item_title:
              data_versionInfo.catalogueProperty?.collection_item_title ||
              'collection_item_title_rtitle',
            collection_item_no_display:
              data_versionInfo.catalogueProperty?.collection_item_no_display ||
              'collection_item_no_display_show',
            collection_item_image_display:
              data_versionInfo.catalogueProperty?.collection_item_image_display ||
              'collection_item_image_display_show',
            collection_item_descr_display:
              data_versionInfo.catalogueProperty?.collection_item_descr_display ||
              'collection_item_descr_display_show',
            collection_view:
              data_versionInfo.catalogueProperty?.collection_view || 'collection_view_list',
          };

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_versions: data_resourceInfo.resourceVersions
          .map((v) => {
            return v.version;
          })
          .reverse(),
        version_resourceInfo: {
          resourceID: data_versionInfo.resourceId,
          resourceName: data_versionInfo.resourceName,
          resourceType: data_versionInfo.resourceType,
          isFinish: data_resourceInfo.serializeStatus === 1,
          isAutomatic: data_CollectionCollectRules?.status === 1 || false,
          // RSS 字段任一非空 => RSS 合集；否则已有单品视为普通合集，空合集保持未定以展示添加方式选择
          rssSource: isRssRelated
            ? 'yes'
            : data_collectionItems.totalItem > 0
            ? 'no'
            : 'unknown',
          itemsTotalCount: data_collectionItems.totalItem,
          updateDate: dayjs(data_versionInfo.updateDate).format(FUtil.Predefined.momentDateFormat),
        },
        version_descriptionText:
          isRssRelated || !data_draft
            ? data_versionInfo.description
            : data_draft.draftData.descriptionEditorInput,
        version_systemProperties: result.systemProperties,
        version_customProperties: result.customProperties,
        version_customConfigurations: result.customConfigurations,
        version_directDependencies:
          isRssRelated || !data_draft
            ? data_versionInfo.dependencies.map((r) => {
                return {
                  id: r.resourceId,
                  name: r.resourceName,
                  type: 'resource' as const,
                  versionRange: r.versionRange,
                };
              })
            : data_draft.draftData.directDependencies,
        version_baseUpcastResources:
          isRssRelated || !data_draft
            ? data_resourceInfo.baseUpcastResources.map((b) => {
                return {
                  resourceID: b.resourceId,
                  resourceName: b.resourceName,
                };
              })
            : data_draft.draftData.baseUpcastResources,
        version_reload: collectionManager.version_reload + 1,
        version_collectionItemsSetting: version_collectionItemsSetting,
        version_resources_pageSize:
          version_collectionItemsSetting.collection_view === 'collection_view_card' ? 6 : 10,
        version_draftSaveTime:
          !isRssRelated && data_draft
            ? dayjs(data_draft.updateDate).format('YYYY-MM-DD HH:mm:ss')
            : '',
        version_collectionItemsChanged:
          (!isRssRelated && data_draft && data_draft.draftData.collectionItemsChanged) || false,
        version_otherChanged:
          (!isRssRelated && data_draft && data_draft.draftData.otherChanged) || false,
      },
    });

    yield put<Version_Fetch_CollectionItems_Action>({
      type: 'version_Fetch_CollectionItems',
    });
    yield call(FUtil.Tool.promiseSleep, 1000);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_pageState: 'loaded',
      },
    });
  },
  *version_Fetch_CollectionItems(
    {}: Version_Fetch_CollectionItems_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    let data_collectionItems: {
      createDate: string;
      itemId: string;
      itemTitle: string;
      sortId: number;
      number: string;
      mountResourceInfo: {
        resourceId: string;
      };
    }[] = [];

    const params: Parameters<typeof FServiceAPI.Resource.getCollectionItems_Draft>[0] = {
      resourceId: collectionManager.version_resourceID,
      skip:
        (collectionManager.version_resources_pageCurrent - 1) *
        collectionManager.version_resources_pageSize,
      limit: collectionManager.version_resources_pageSize,
      keywords: collectionManager.version_resourceKeywords,
      sortType:
        collectionManager.version_collectionItemsSetting?.collection_sort_list ===
        'collection_sort_descending'
          ? -1
          : 1,
      isLoadLatestVersionInfo: 1,
    };

    const {
      ret,
      errCode,
      data,
    }: {
      ret: number;
      errCode: number;
      data: {
        skip: number;
        limit: number;
        totalItem: number;
        dataList: {
          createDate: string;
          itemId: string;
          itemTitle: string;
          sortId: number;
          number: string;
          mountResourceInfo: {
            resourceId: string;
          };
        }[];
      };
    } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, params);
    data_collectionItems = data.dataList;
    if (
      data.dataList.length === 0 &&
      collectionManager.version_resources_pageCurrent > 1 &&
      data.totalItem > 0
    ) {
      const version_resources_pageCurrent: number =
        Math.floor((data.totalItem - 1) / collectionManager.version_resources_pageSize) + 1;
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_resources_pageCurrent: version_resources_pageCurrent,
        },
      });

      const parmas: Parameters<typeof FServiceAPI.Resource.getCollectionItems_Draft>[0] = {
        resourceId: collectionManager.version_resourceID,
        skip: (version_resources_pageCurrent - 1) * collectionManager.version_resources_pageSize,
        limit: collectionManager.version_resources_pageSize,
        keywords: collectionManager.version_resourceKeywords,
        sortType:
          collectionManager.version_collectionItemsSetting?.collection_sort_list ===
          'collection_sort_descending'
            ? -1
            : 1,
        isLoadLatestVersionInfo: 1,
      };

      const {
        data: data1,
      }: {
        data: {
          dataList: {
            createDate: string;
            itemId: string;
            itemTitle: string;
            sortId: number;
            number: string;
            mountResourceInfo: {
              resourceId: string;
            };
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, parmas);

      data_collectionItems = data1.dataList;
    }

    let data_resourceList: any[] = [];
    let auths: {
      itemId: string;
      isAuth: boolean;
    }[] = [];

    if (data_collectionItems.length > 0) {
      const { data: data_resourceList1 } = yield call(FServiceAPI.Resource.batchInfo, {
        resourceIds: data_collectionItems
          .map((d) => {
            return d.mountResourceInfo.resourceId;
          })
          .join(','),
      });
      data_resourceList = data_resourceList1;

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_collectionItems: data_collectionItems.map<
            CollectionManagerState['version_collectionItems'][number]
          >((i, j: number) => {
            const resource = data_resourceList.find((r) => {
              return r.resourceId === i.mountResourceInfo.resourceId;
            });
            return {
              itemID: i.itemId,
              itemTitle: i.itemTitle,
              itemSerial: i.sortId,
              itemNumber: i.number,
              resourceInfo: {
                id: resource.resourceId,
                cover: resource.coverImages.length > 0 ? resource.coverImages[0] : '',
                name: resource.resourceName,
                title: resource.resourceTitle,
                version: resource.latestVersion,
                policy: resource.policies
                  .filter((l: any) => {
                    return l.status === 1;
                  })
                  .map((l: any) => l.policyName),
                type: resource.resourceType,
                status: resource.status,
                authProblem: false,
                updateDate: dayjs(resource.updateDate).format(FUtil.Predefined.momentDateFormat),
                username: resource.username,
                useAvatar: `${AVATAR_URL_PREFIX}${resource.userId}`,
                isChoice: resource.operationType === 5,
                intro: resource.intro,
                releaseDateTime: dayjs(resource.resourceVersions[0]?.createDate).format(
                  'YYYY-MM-DD',
                ),
              },
            };
          }),
          version_resources_totalCount: data.totalItem,
          version_resources_state:
            data.totalItem > 0
              ? 'loaded'
              : collectionManager.version_resourceKeywords === ''
              ? 'noData'
              : 'noSearchResult',
        },
      });

      const parmas1: Parameters<typeof FServiceAPI.Resource.getCollectionItemsAuth_Draft>[0] = {
        resourceId: collectionManager.version_resourceID,
        itemIds: data_collectionItems
          .map((d) => {
            return d.itemId;
          })
          .join(','),
      };
      const {
        data: data1,
      }: {
        data: {
          itemId: string;
          isAuth: boolean;
        }[];
      } = yield call(FServiceAPI.Resource.getCollectionItemsAuth_Draft, parmas1);
      auths = data1;
    }

    const list: CollectionManagerState['version_collectionItems'] = data_collectionItems.map<
      CollectionManagerState['version_collectionItems'][number]
    >((i, j: number) => {
      const resAuth = auths.find((dd) => {
        return dd.itemId === i.itemId;
      });

      const resource = data_resourceList.find((r) => {
        return r.resourceId === i.mountResourceInfo.resourceId;
      });
      return {
        itemID: i.itemId,
        itemTitle: i.itemTitle,
        itemSerial: i.sortId,
        itemNumber: i.number,
        resourceInfo: {
          id: resource.resourceId,
          cover: resource.coverImages.length > 0 ? resource.coverImages[0] : '',
          name: resource.resourceName,
          title: resource.resourceTitle,
          version: resource.latestVersion,
          policy: resource.policies
            .filter((l: any) => {
              return l.status === 1;
            })
            .map((l: any) => l.policyName),
          type: resource.resourceType,
          status: resource.status,
          authProblem: !!resAuth && !resAuth.isAuth,
          updateDate: dayjs(resource.updateDate).format(FUtil.Predefined.momentDateFormat),
          username: resource.username,
          useAvatar: `${AVATAR_URL_PREFIX}${resource.userId}`,
          isChoice: resource.operationType === 5,
          intro: resource.intro,
          releaseDateTime: dayjs(resource.resourceVersions[0]?.createDate).format(FUtil.Predefined.momentDateFormat),
        },
      };
    });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_collectionItems: list,
        version_resources_totalCount: data.totalItem,
        version_resources_state:
          data.totalItem > 0
            ? 'loaded'
            : collectionManager.version_resourceKeywords === ''
            ? 'noData'
            : 'noSearchResult',
      },
    });
  },
  *version_syncAllProperties(
    {}: Version_SyncAllPropertiesAction,
    { select, call }: EffectsCommandMap,
  ) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    const authExcludedItems: {
      resourceId: string;
      excludedType: 'contractId' | 'policyId';
      excludedValue: string;
    }[] = collectionManager.version_authExcludedItems.flatMap((r0) => {
      return r0.contracts.map((r1) => {
        return {
          resourceId: r0.resourceID,
          excludedType: 'contractId' as const,
          excludedValue: r1.contractID,
        };
      });
    });

    const params: Parameters<typeof FServiceAPI.Resource.updateCollection>[0] = {
      resourceId: collectionManager.version_resourceID,
      authExcludedItems: authExcludedItems,
      customPropertyDescriptors: [
        ...collectionManager.version_customProperties.map((i) => {
          return {
            type: 'readonlyText',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: i.value,
          };
        }),
        ...collectionManager.version_customConfigurations.map((i) => {
          const isInput: boolean = i.type === 'input';
          const options: string[] = i.select;
          return {
            type: isInput ? 'editableText' : 'select',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: isInput ? i.input : options[0],
            candidateItems: isInput ? undefined : options,
          };
        }),
      ],
    };

    yield call(FServiceAPI.Resource.updateCollection, params);
  },
  *version_SaveDraft({}: Version_SaveDraft_Action, { select, call, put }: EffectsCommandMap) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    // RSS 合集停用草稿保存
    if (collectionManager.version_resourceInfo?.rssSource === 'yes') {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_draftSaving: true,
      },
    });

    const params1: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
      resourceId: collectionManager.version_resourceID,
    };
    const {
      data: data_draft1,
    }: {
      data: null | {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.lookDraft, params1);

    const draftData: ICollectionCreateVersionDraftType = {
      ...(data_draft1?.draftData || {}),
      collectionItemsSetting: collectionManager.version_collectionItemsSetting,
      additionalProperties: collectionManager.version_systemProperties
        .filter((sp) => sp.type === 'additional')
        .map((sp) => {
          return {
            key: sp.key,
            value: sp.value,
          };
        }),
      customProperties: collectionManager.version_customProperties,
      customConfigurations: collectionManager.version_customConfigurations,
      directDependencies: collectionManager.version_directDependencies,
      baseUpcastResources: collectionManager.version_baseUpcastResources,
      descriptionEditorInput: collectionManager.version_descriptionText,
      collectionItemsChanged: collectionManager.version_collectionItemsChanged,
      otherChanged: collectionManager.version_otherChanged,
    };

    const params: Parameters<typeof FServiceAPI.Resource.saveVersionsDraft>[0] = {
      resourceId: collectionManager.version_resourceID,
      draftData: draftData,
    };
    const {
      ret,
      errCode,
      data: data_draft,
    }: {
      ret: number;
      errCode: number;
      data: {
        resourceId: string;
        updateDate: string;
        draftData: IResourceCreateVersionDraftType;
      };
    } = yield call(FServiceAPI.Resource.saveVersionsDraft, params);
    if (ret !== 0 || errCode !== 0) {
      fMessage('草稿保存失败', 'error');
      return;
    }
    yield call(FUtil.Tool.promiseSleep, 500);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_draftSaving: false,
        version_draftSaveTime: dayjs(data_draft.updateDate).format('YYYY-MM-DD HH:mm:ss'),
      },
    });
  },
  *version_setRssSource(
    { payload }: Version_SetRssSource_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    if (!collectionManager.version_resourceInfo) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        version_resourceInfo: {
          ...collectionManager.version_resourceInfo,
          rssSource: payload.rssSource,
        },
      },
    });
  },
  *version_SaveDate(action: Version_SaveDate_Action, { select, call, put }: EffectsCommandMap) {
    const { collectionManager }: ConnectState = yield select(
      ({ collectionManager }: ConnectState) => ({
        collectionManager,
      }),
    );

    const authExcludedItems: {
      resourceId: string;
      excludedType: 'contractId' | 'policyId';
      excludedValue: string;
    }[] = collectionManager.version_authExcludedItems.flatMap((r0) => {
      return r0.contracts.map((r1) => {
        return {
          resourceId: r0.resourceID,
          excludedType: 'contractId' as const,
          excludedValue: r1.contractID,
        };
      });
    });

    const params: Parameters<typeof FServiceAPI.Resource.updateCollection>[0] = {
      resourceId: collectionManager.version_resourceID,
      inputAttrs: collectionManager.version_systemProperties
        .filter((sp) => sp.type === 'additional')
        .map((sp) => {
          return {
            key: sp.key,
            value: sp.value,
          };
        }),
      customPropertyDescriptors: [
        ...collectionManager.version_customProperties.map((i) => {
          return {
            type: 'readonlyText',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: i.value,
          };
        }),
        ...collectionManager.version_customConfigurations.map((i) => {
          const isInput: boolean = i.type === 'input';
          const options: string[] = i.select;
          return {
            type: isInput ? 'editableText' : 'select',
            key: i.key,
            name: i.name,
            remark: i.description,
            defaultValue: isInput ? i.input : options[0],
            candidateItems: isInput ? undefined : options,
          };
        }),
      ],
      description: collectionManager.version_descriptionText,
      catalogueProperty: collectionManager.version_collectionItemsSetting,
      dependencies: collectionManager.version_directDependencies.map((r) => {
        return {
          resourceId: r.id,
          versionRange: r.versionRange || '',
        };
      }),
      // @ts-ignore
      authExcludedItems: authExcludedItems,
      isMergeCatalogueDraft: collectionManager.version_collectionItemsChanged ? 1 : 0,
    };

    const {
      ret,
      errCode,
      msg,
    } = yield call(FServiceAPI.Resource.updateCollection, params);

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return false;
    }

    fMessage(FI18n.i18nNext.tAuto('edit_collection_itemmgnt_msg_released_successfully'), 'success');
    return true;
  },
};
